// Como se muestran los estados y tipos que la base guarda en MAYUSCULAS.
// Vive aparte porque lo usan las dos pantallas.

import { COLORES_PROYECTO } from "../lib/tipos";
import type {
  ColorProyecto,
  EstadoProyecto,
  EstadoEfectivo,
  TipoTarea,
  Periodicidad,
  EstadoPresupuestoEfectivo,
  EstadoContrato,
  CategoriaCondicion,
  Destinatario,
} from "../lib/tipos";
import type { Urgencia } from "../lib/fecha";

export const ETIQUETA_PERIODICIDAD: Record<Periodicidad, string> = {
  UNICO: "Una vez",
  MENSUAL: "Mensual",
  TRIMESTRAL: "Trimestral",
  ANUAL: "Anual",
};

export const ETIQUETA_ESTADO_PROYECTO: Record<EstadoProyecto, string> = {
  IDEA: "Idea",
  EN_DESARROLLO: "En desarrollo",
  EN_PRODUCCION: "En producción",
  PAUSADO: "Pausado",
  ARCHIVADO: "Archivado",
};

/** Nombres de los colores, para el que elige y para los lectores de pantalla. */
export const ETIQUETA_COLOR: Record<ColorProyecto, string> = {
  AZUL: "Azul",
  CIAN: "Cian",
  VERDE: "Verde",
  LIMA: "Lima",
  AMBAR: "Ámbar",
  NARANJA: "Naranja",
  ROSA: "Rosa",
  VIOLETA: "Violeta",
};

/**
 * El atributo que enciende el color propio del proyecto.
 *
 * El tono sale de `[data-color]` en app.css: acá nunca se escribe un hex, así
 * la paleta queda en un solo lugar. Sin color no se pone el atributo y la
 * tarjeta se ve como siempre.
 */
export function atributoColor(color: ColorProyecto | null | undefined): string {
  return color ? ` data-color="${color}"` : "";
}

/**
 * Los nueve puntitos para elegir el color de un proyecto: el de "sin color" y
 * uno por cada color de la paleta.
 *
 * Vive acá, al lado del catálogo, porque lo usan las dos pantallas que tocan
 * un proyecto: el alta de la lista y la ficha. Se lee con `FormData`, donde el
 * "sin color" llega como cadena vacía y hay que mandarlo como null.
 */
export function selectorColor(actual: ColorProyecto | null, campo = "color"): string {
  const punto = (valor: string, etiqueta: string, vacio: boolean) => `
    <label class="muestra" title="${etiqueta}">
      <input type="radio" name="${campo}" value="${valor}" aria-label="${etiqueta}"
             ${valor === (actual ?? "") ? "checked" : ""} />
      <span class="muestra-punto${vacio ? " muestra-punto-vacio" : ""}"${
        valor ? ` data-color="${valor}"` : ""
      }></span>
    </label>`;
  return `
    <div class="flex flex-wrap items-center gap-3">
      ${punto("", "Sin color", true)}
      ${COLORES_PROYECTO.map((c) => punto(c, ETIQUETA_COLOR[c], false)).join("")}
    </div>`;
}

export const ETIQUETA_TIPO_TAREA: Record<TipoTarea, string> = {
  MODIFICACION: "Modificación",
  BUG: "Bug",
  MEJORA: "Mejora",
  TRAMITE: "Trámite",
  PAGO: "Pago",
  INVESTIGACION: "Investigación",
};

export const ETIQUETA_CATEGORIA: Record<CategoriaCondicion, string> = {
  PAGO: "Pago",
  PLAZOS: "Plazos",
  ALCANCE: "Alcance",
  SOPORTE: "Soporte",
  PROPIEDAD: "Propiedad",
  HOSTING: "Hosting",
};

/** Quién tiene que hacer la tarea. En la app, "yo" es Fernando. */
export const ETIQUETA_DESTINATARIO: Record<Destinatario, string> = {
  DESARROLLADOR: "yo",
  CLIENTE: "cliente",
};

export const ETIQUETA_ESTADO_PRESUPUESTO: Record<EstadoPresupuestoEfectivo, string> = {
  BORRADOR: "Borrador",
  ENVIADO: "Enviado",
  ACEPTADO: "Aceptado",
  RECHAZADO: "Rechazado",
  ANULADO: "Anulado",
  VENCIDO: "Vencido",
};

export function claseEstadoPresupuesto(estado: EstadoPresupuestoEfectivo): string {
  if (estado === "ACEPTADO") return "chip chip-ok";
  if (estado === "ENVIADO") return "chip chip-acento";
  if (estado === "VENCIDO") return "chip chip-alerta";
  return "chip";
}

export const ETIQUETA_ESTADO_CONTRATO: Record<EstadoContrato, string> = {
  BORRADOR: "Borrador",
  ENVIADO: "Enviado",
  FIRMADO: "Firmado",
  ANULADO: "Anulado",
};

/** Mismo criterio que el presupuesto: firmado descansa, enviado espera. */
export function claseEstadoContrato(estado: EstadoContrato): string {
  if (estado === "FIRMADO") return "chip chip-ok";
  if (estado === "ENVIADO") return "chip chip-acento";
  return "chip";
}

/** Clase del chip de estado del proyecto. */
export function claseEstadoProyecto(estado: EstadoProyecto): string {
  if (estado === "EN_PRODUCCION") return "chip chip-ok";
  if (estado === "EN_DESARROLLO") return "chip chip-acento";
  return "chip";
}

/**
 * Trámite y pago se destacan: son los que suelen frenar todo lo demás.
 *
 * El resto va en neutro, incluidos los bugs. El color de alarma se reserva
 * para lo que corre por tiempo (vencido, hoy, prioridad 1): si un bug de baja
 * prioridad y sin fecha se pinta igual que algo vencido, el rojo deja de
 * querer decir "esto arde".
 */
export function claseTipoTarea(tipo: TipoTarea): string {
  if (tipo === "TRAMITE" || tipo === "PAGO") return "chip chip-acento";
  return "chip";
}

export function claseUrgencia(u: Urgencia): string {
  if (u === "VENCIDO" || u === "HOY") return "chip chip-alerta";
  if (u === "SEMANA") return "chip chip-acento";
  return "chip";
}

export function textoUrgencia(u: Urgencia, fecha: string): string {
  if (u === "VENCIDO") return `venció ${fecha}`;
  if (u === "HOY") return "vence hoy";
  return `vence ${fecha}`;
}

/**
 * Prioridad 1 es lo más importante, 10 lo que puede esperar.
 *
 * Con la escala de 1 a 10 se pintan las tres primeras y nada más: si se
 * pintara la mitad de la escala, volveríamos a tener una pantalla donde todo
 * grita. De 4 para abajo va en neutro.
 */
export function clasePrioridad(prioridad: number): string {
  if (prioridad <= 2) return "chip chip-alerta";
  if (prioridad === 3) return "chip chip-acento";
  return "chip";
}

export function etiquetaEstadoTarea(estado: EstadoEfectivo): string {
  if (estado === "EN_CURSO") return "En curso";
  if (estado === "BLOQUEADA") return "Bloqueada";
  if (estado === "HECHA") return "Hecha";
  if (estado === "ANULADA") return "Anulada";
  return "Pendiente";
}
